import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  SelectChangeEvent
} from '@mui/material';
import {
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { PDFFile, PDFFileManagerRef } from '../types';
import { getPDFFiles, downloadPDF, deletePDF } from '../services/api';
import { formatFileSize, formatDate, downloadBlob } from '../utils/formatters';
import { ERROR_MESSAGES } from '../constants';
import PageContainer from './common/PageContainer';
import CustomPagination from './common/CustomPagination';

const PDFFileManager = forwardRef<PDFFileManagerRef>((props, ref) => {
  const [files, setFiles] = useState<PDFFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getPDFFiles();
      if (response.success) {
        setFiles(response.files);
      } else {
        throw new Error(response.error || ERROR_MESSAGES.FETCH_FILES_FAILED);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.FETCH_FILES_FAILED;
      setError(errorMessage);
      console.error('獲取文件列表失敗:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (filename: string) => {
    try {
      setDownloadingFiles(prev => new Set(prev).add(filename));
      
      const blob = await downloadPDF(filename);
      downloadBlob(blob, filename);
      
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '下載失敗';
      console.error('下載文件失敗:', err);
      alert(`下載失敗: ${errorMessage}`);
    } finally {
      setDownloadingFiles(prev => {
        const newSet = new Set(prev);
        newSet.delete(filename);
        return newSet;
      });
    }
  };

  const handleDelete = async (filename: string) => {
    const confirmDelete = window.confirm(`確定要刪除文件 ${filename} 嗎？`);
    
    if (confirmDelete) {
      try {
        setDeletingFiles(prev => new Set(prev).add(filename));
        
        const result = await deletePDF(filename);
        
        if (result.success) {
          setFiles(prev => prev.filter(file => file.filename !== filename));
        } else {
          throw new Error(result.error || ERROR_MESSAGES.DELETE_FILE_FAILED);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : ERROR_MESSAGES.DELETE_FILE_FAILED;
        console.error('刪除文件失敗:', err);
        alert(`刪除失敗: ${errorMessage}`);
      } finally {
        setDeletingFiles(prev => {
          const newSet = new Set(prev);
          newSet.delete(filename);
          return newSet;
        });
      }
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  useImperativeHandle(ref, () => ({
    fetchFiles
  }));

  useEffect(() => {
    fetchFiles();
  }, []);

  if (loading) {
    return (
      <PageContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            加載中...
          </Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer maxWidth="lg">
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight={700}>
            PDF文件列表
          </Typography>
          <Typography variant="body1" color="text.secondary">
            管理所有生成的PDF文檔
          </Typography>
        </Box>
        <Tooltip title="刷新列表">
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchFiles}
            sx={{ borderRadius: 2 }}
          >
            刷新
          </Button>
        </Tooltip>
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button color="inherit" size="small" onClick={fetchFiles}>
              重試
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {files.length === 0 ? (
        <Paper 
          sx={{ 
            p: 8, 
            textAlign: 'center',
            border: '2px dashed',
            borderColor: 'divider',
            backgroundColor: 'background.paper',
            borderRadius: 3
          }}
        >
          <PdfIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            暫無PDF文件
          </Typography>
          <Typography variant="body2" color="text.secondary">
            生成第一個PDF文檔吧！
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 3 }}>
          <TableContainer>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>文件名</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>大小</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>創建時間</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>狀態</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600, bgcolor: 'grey.50' }}>操作</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {files
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((file, index) => {
                    const isDownloading = downloadingFiles.has(file.filename);
                    const isDeleting = deletingFiles.has(file.filename);
                    
                    return (
                      <TableRow 
                        key={index} 
                        hover
                        sx={{ 
                          '&:last-child td': { border: 0 },
                          transition: 'background-color 0.2s'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PdfIcon sx={{ mr: 1.5, color: 'error.main' }} />
                            <Typography variant="body2" fontWeight={500}>
                              {file.filename}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={formatFileSize(file.size)} 
                            size="small" 
                            variant="outlined"
                            sx={{ borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" color="text.secondary">
                            {formatDate(file.created)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label="PDF文檔" 
                            color="error" 
                            size="small"
                            sx={{ borderRadius: 2 }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Tooltip title="下載">
                              <span>
                                <IconButton
                                  color="primary"
                                  onClick={() => handleDownload(file.filename)}
                                  disabled={isDownloading}
                                  size="small"
                                >
                                  {isDownloading ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DownloadIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="刪除">
                              <span>
                                <IconButton
                                  color="error"
                                  onClick={() => handleDelete(file.filename)}
                                  disabled={isDeleting}
                                  size="small"
                                >
                                  {isDeleting ? (
                                    <CircularProgress size={20} />
                                  ) : (
                                    <DeleteIcon />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
          </TableContainer>
          <CustomPagination
            count={files.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[5, 10, 25, 50]}
            labelRowsPerPage="每頁行數:"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} 共 ${count} 條`}
          />
        </Paper>
      )}
    </PageContainer>
  );
});

PDFFileManager.displayName = 'PDFFileManager';

export default PDFFileManager;
